import styled from '@emotion/styled';
import { Color, TextInputField } from '@liveoptics/react-ui';
import { useClickAway, useRequest } from 'ahooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const Container = styled.div<{ isLoading: boolean; open: boolean }>`
  position: relative;
  input {
    overflow: hidden !important;
    white-space: pre !important;
    text-overflow: ellipsis !important;
    -webkit-appearance: none !important;
    padding-right: 30px !important;
  }
  div > div:after {
    float: right;
    position: relative;
    top: -34px;
    right: 10px;
    width: 20px;
    height: 20px;
    content: '';
    background-image: ${(props) =>
      props.isLoading
        ? ''
        : `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 150 150' fill='%23AAAAAA' width='20' height='20'><path d='M147.827 135.523L116.133 103.843C126.063 90.7972 130.667 74.4668 129.015 58.1553C127.363 41.8438 119.578 26.7685 107.234 15.9788C94.8898 5.18902 78.909 -0.509102 62.5232 0.0357615C46.1373 0.580625 30.5697 7.32801 18.9697 18.9139C7.36968 30.4999 0.603194 46.0593 0.0384212 62.4445C-0.526352 78.8297 5.15267 94.8173 15.9274 107.174C26.7021 119.532 41.7679 127.336 58.0774 129.008C74.3868 130.68 90.7228 126.095 103.78 116.18L135.475 147.86C137.112 149.499 139.333 150.419 141.649 150.419C143.964 150.42 146.186 149.501 147.824 147.864C149.462 146.227 150.383 144.007 150.384 141.691C150.384 139.375 149.464 137.154 147.827 135.516V135.523ZM17.8381 64.9329C17.8307 55.6351 20.5807 46.5437 25.7409 38.8093C30.9011 31.0748 38.2394 25.0443 46.8274 21.4811C55.4153 17.9178 64.8664 16.9819 73.9865 18.7913C83.1065 20.6006 91.4858 25.0743 98.063 31.6462C104.64 38.2181 109.121 46.5931 110.937 55.7117C112.754 64.8303 111.825 74.2828 108.268 82.8736C104.712 91.4644 98.6879 98.8074 90.9576 103.974C83.2273 109.14 74.1382 111.898 64.8404 111.898C52.3886 111.888 40.4484 106.941 31.6388 98.1409C22.8291 89.3412 17.8693 77.4066 17.8454 64.9549L17.8381 64.9329Z' /></svg>")`};
    border: ${(props) => (props.isLoading ? `3px solid ${Color.coolGray300}` : 'none')};
    border-radius: ${(props) => (props.isLoading ? '50%' : '0')};
    border-top-color: ${(props) => (props.isLoading ? Color.blueDell : 'transparent')};
    animation: ${(props) => (props.isLoading ? 'rotating 1s linear infinite' : '')};

    @keyframes rotating {
      from {
        -ms-transform: rotate(0deg);
        -moz-transform: rotate(0deg);
        -webkit-transform: rotate(0deg);
        -o-transform: rotate(0deg);
        transform: rotate(0deg);
      }
      to {
        -ms-transform: rotate(360deg);
        -moz-transform: rotate(360deg);
        -webkit-transform: rotate(360deg);
        -o-transform: rotate(360deg);
        transform: rotate(360deg);
      }
    }
  }
  &:focus-within {
    div > div:after {
      background-image: ${(props) =>
        props.isLoading
          ? ''
          : `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 150 150' fill='%23${Color.blue500.slice(
              1
            )}' width='20' height='20'><path d='M147.827 135.523L116.133 103.843C126.063 90.7972 130.667 74.4668 129.015 58.1553C127.363 41.8438 119.578 26.7685 107.234 15.9788C94.8898 5.18902 78.909 -0.509102 62.5232 0.0357615C46.1373 0.580625 30.5697 7.32801 18.9697 18.9139C7.36968 30.4999 0.603194 46.0593 0.0384212 62.4445C-0.526352 78.8297 5.15267 94.8173 15.9274 107.174C26.7021 119.532 41.7679 127.336 58.0774 129.008C74.3868 130.68 90.7228 126.095 103.78 116.18L135.475 147.86C137.112 149.499 139.333 150.419 141.649 150.419C143.964 150.42 146.186 149.501 147.824 147.864C149.462 146.227 150.383 144.007 150.384 141.691C150.384 139.375 149.464 137.154 147.827 135.516V135.523ZM17.8381 64.9329C17.8307 55.6351 20.5807 46.5437 25.7409 38.8093C30.9011 31.0748 38.2394 25.0443 46.8274 21.4811C55.4153 17.9178 64.8664 16.9819 73.9865 18.7913C83.1065 20.6006 91.4858 25.0743 98.063 31.6462C104.64 38.2181 109.121 46.5931 110.937 55.7117C112.754 64.8303 111.825 74.2828 108.268 82.8736C104.712 91.4644 98.6879 98.8074 90.9576 103.974C83.2273 109.14 74.1382 111.898 64.8404 111.898C52.3886 111.888 40.4484 106.941 31.6388 98.1409C22.8291 89.3412 17.8693 77.4066 17.8454 64.9549L17.8381 64.9329Z' /></svg>")`};
    }
    input {
      border: 2px solid ${Color.blue500};
    }

    ul {
      border-color: ${Color.blue500};
    }
  }
`;

const SearchList = styled.ul`
  box-sizing: border-box;
  list-style: none;
  border: 2px solid ${Color.coolGray300};
  border-top: none;
  margin: 0px;
  padding: 3px;
  position: absolute;
  background-color: white;
  max-height: 200px;
  z-index: 1000;
  overflow-y: scroll;
  width: 100%;
  li {
    font-size: 14px;
    line-height: 35px;
    color: ${Color.coolGray700};
    padding: 0px 2px;
    overflow: hidden !important;
    white-space: pre !important;
    text-overflow: ellipsis !important;
    -webkit-appearance: none !important;

    &[aria-selected='true'] {
      background-color: ${Color.green100};
    }
    &[aria-current='true'] {
      background-color: ${Color.blue100};
      cursor: pointer;
    }
    &[aria-selected='true'] {
      &[aria-current='true'] {
        background-color: ${Color.green100};
      }
    }
    &:hover {
      background-color: ${Color.blue100};
      cursor: pointer;
    }
  }
`;

export interface AutoCompleteItem {
  id: string;
  value: string;
}
export type AutoCompleteProps = React.HTMLProps<HTMLInputElement> & {
  defaultValue?: string;
  lazy: boolean;
  renderValue: (item: AutoCompleteItem) => string;
  fetchDataCallback: (keyword?: string) => Promise<AutoCompleteItem[]>;
  onInputValueChange?: (value?: string) => void;
  onSelectionChange?: (item?: AutoCompleteItem) => void;
  onDropDownToggle?: (show: boolean) => void;
};

export const AutoComplete: React.FC<AutoCompleteProps> = (props: AutoCompleteProps) => {
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [focused, setFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [currentSelectedIndex, setCurrentSelectedIndex] = useState<number | null>(null);
  const [currentValue, setCurrentValue] = useState<string | undefined>(props.defaultValue);

  const autoComplete = useRef<HTMLDivElement>(null);
  const itemRefs: HTMLLIElement[] = useMemo(() => [], []);

  const request = useRequest(props.fetchDataCallback, {
    manual: true,
    debounceWait: 500,
  });

  useEffect(() => {
    if (props.onDropDownToggle) props.onDropDownToggle(showDropdownMenu);
  }, [props, showDropdownMenu]);

  useEffect(() => {
    if (focused && !props.lazy && highlightedIndex === null) {
      request.run(currentValue);
    }
  }, [currentValue, focused, highlightedIndex, props.lazy]);

  useEffect(() => {
    const closeOnEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setShowDropdownMenu(false);
      }
    };
    window.addEventListener('keydown', closeOnEsc);
    return () => window.removeEventListener('keydown', closeOnEsc);
  }, []);

  useClickAway(() => {
    setShowDropdownMenu(false);
    setFocused(false);
  }, autoComplete);

  const handleInputChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      e.preventDefault();
      if (value.length === 0 && props.lazy) setShowDropdownMenu(false);
      else if (value.length > 0 || (value.length === 0 && !props.lazy)) {
        setShowDropdownMenu(false);
        request.run(value);
        setShowDropdownMenu(true);
      }
      setHighlightedIndex(null);
      setCurrentSelectedIndex(null);
      if (props.onSelectionChange) props.onSelectionChange(undefined);
      setCurrentValue(value);
      if (props.onInputValueChange) props.onInputValueChange(value);
    },
    [props, request]
  );

  const selectIndex = useCallback(
    (index: number) => {
      if (request.data) {
        const item = request.data[index];
        setCurrentSelectedIndex(index);
        setCurrentValue(props.renderValue(item));
        setShowDropdownMenu(false);
        if (props.onSelectionChange) props.onSelectionChange(item);
      }
    },
    [props, request.data]
  );

  const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const iKeyCode = e.key;
    switch (iKeyCode) {
      case 'Tab':
      case 'Enter':
      case 'Escape':
      case 'ArrowUp':
      case 'ArrowDown':
        e.preventDefault();
        return true;
    }
  }, []);

  const moveUp = useCallback(() => {
    if (request.data) {
      if (highlightedIndex && highlightedIndex > 0) {
        setHighlightedIndex(highlightedIndex - 1);
      }
      if (highlightedIndex !== null) itemRefs[highlightedIndex].scrollIntoView();
    }
  }, [highlightedIndex, itemRefs, request.data]);

  const moveDown = useCallback(() => {
    if (request.data) {
      if (highlightedIndex === null) setHighlightedIndex(0);
      else if (highlightedIndex < request.data.length - 1) {
        setHighlightedIndex(highlightedIndex + 1);
      }
      if (highlightedIndex !== null) itemRefs[highlightedIndex].scrollIntoView();
    }
  }, [highlightedIndex, itemRefs, request.data]);

  const handlekeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const iKeyCode = e.key;
      handleKeyUp(e);
      switch (iKeyCode) {
        case 'Tab':
          if (e.shiftKey) {
            moveUp();
            break;
          }
          moveDown();
          break;
        case 'Enter':
          if (!showDropdownMenu) setShowDropdownMenu(true);
          else if (highlightedIndex !== null) selectIndex(highlightedIndex);
          break;
        case 'Escape':
          setShowDropdownMenu(false);
          break;
        case 'ArrowUp':
          moveUp();
          break;
        case 'ArrowDown':
          if (!showDropdownMenu) setShowDropdownMenu(true);
          else moveDown();
          break;
        default:
          break;
      }
      return false;
    },
    [handleKeyUp, highlightedIndex, moveDown, moveUp, selectIndex, showDropdownMenu]
  );

  const handleFocus = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setFocused(true);
      setCurrentValue(e.currentTarget.value);
      if (e.currentTarget.value.length > 0 && e.currentTarget.value === currentValue)
        setHighlightedIndex(currentSelectedIndex);
      setShowDropdownMenu(true);
    },
    [currentSelectedIndex, currentValue]
  );

  const handleBlur = useCallback(() => setFocused(false), []);

  const value =
    currentSelectedIndex && request.data
      ? props.renderValue(request.data[currentSelectedIndex])
      : currentValue;
  return (
    <Container
      ref={autoComplete}
      isLoading={request.loading ? true : false}
      className={props.className}
      open={showDropdownMenu}
    >
      <TextInputField
        title={value}
        label=""
        placeholder={props.placeholder}
        defaultValue={props.defaultValue}
        status={props.disabled ? 'readonly' : 'active'}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onKeyUp={handleKeyUp}
        onKeyDown={handlekeyDown}
        value={value}
      />
      {request.data && request.data.length > 0 && showDropdownMenu && !request.loading && (
        <SearchList>
          {request.data.map((item: AutoCompleteItem, index: number) => {
            const text = props.renderValue(item);
            return (
              <li
                key={item.id}
                aria-current={highlightedIndex === index}
                aria-selected={currentSelectedIndex === index}
                ref={(el) => {
                  if (el) itemRefs[index] = el;
                }}
                title={text}
                onClick={() => selectIndex(index)}
              >
                {text}
              </li>
            );
          })}
        </SearchList>
      )}
    </Container>
  );
};
